import { useEffect, useState } from "react";
import { FiLoader, FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { fetchAppInfo } from "../helpers/global_service";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Custom styles for ReactQuill read-only display
const quillReadOnlyStyles = `
  .react-quill-readonly .ql-editor {
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    font-family: inherit !important;
    line-height: 1.6 !important;
    color: inherit !important;
    font-size: inherit !important;
  }
  
  .react-quill-readonly .ql-container {
    border: none !important;
    font-size: inherit !important;
  }
  
  .react-quill-readonly .ql-toolbar {
    display: none !important;
  }
  
  .react-quill-readonly .ql-editor.ql-blank::before {
    display: none !important;
  }
`;

interface AppInfoResponse {
  statusCode: number;
  message: string;
  response: string;
}

export default function TermsConditions() {
  const { t } = useTranslation();
  const [appInfo, setAppInfo] = useState<AppInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inject ReactQuill read-only styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = quillReadOnlyStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAppInfo({ typeId: 3 });
        setAppInfo(response.data);
      } catch (err) {
        setError(err as string);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRetry = () => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchAppInfo({ typeId: 3 });
        setAppInfo(response.data);
      } catch (err) {
        setError(err as string);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full px-4 py-8">
        {/* Content Container */}
        <div className="w-full max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t("pages.termsConditions.title")}
            </h1>
            <div className="w-20 h-1 bg-primaryColor rounded"></div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 md:p-8">
            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <FiLoader className="h-12 w-12 text-primaryColor animate-spin mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("pages.termsConditions.loading")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center">
                  {t("pages.termsConditions.loadingDescription")}
                </p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <FiAlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("pages.termsConditions.errorTitle")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                  {error}
                </p>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-4 py-2 bg-primaryColor text-white rounded-lg hover:bg-primaryColor/90 transition-colors duration-200"
                >
                  <FiRefreshCw className="h-4 w-4 mr-2" />
                  {t("pages.termsConditions.retryButton")}
                </button>
              </div>
            )}

            {/* Success State - Display API Content */}
            {appInfo && !loading && !error && (
              <div className="react-quill-readonly">
                <ReactQuill
                  value={appInfo.response}
                  readOnly={true}
                  theme="snow"
                  modules={{
                    toolbar: false,
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                  }}
                />
              </div>
            )}

            {/* Fallback Content - Show if API fails and no retry attempted */}
            {!appInfo && !loading && !error && (
              <div className="prose prose-gray dark:prose-invert max-w-none">
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-6">
                  Last updated: {new Date().toLocaleDateString()}
                </p>

                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                  Terms & Conditions
                </h2>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  These Terms and Conditions govern your use of the Bossie
                  application and services. By accessing or using our services,
                  you agree to be bound by these terms.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Acceptance of Terms
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  By using Bossie, you acknowledge that you have read,
                  understood, and agree to be bound by these Terms and
                  Conditions. If you do not agree with any part of these terms,
                  you may not use our services.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Use of Services
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  You may use our services for lawful purposes only. You agree
                  not to:
                </p>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                  <li>
                    Use the services for any illegal or unauthorized purpose
                  </li>
                  <li>Interfere with or disrupt the services or servers</li>
                  <li>
                    Attempt to gain unauthorized access to any part of the
                    services
                  </li>
                  <li>
                    Transmit any harmful, offensive, or inappropriate content
                  </li>
                  <li>Violate any applicable laws or regulations</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  User Accounts
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  You are responsible for maintaining the confidentiality of
                  your account credentials and for all activities that occur
                  under your account. You must notify us immediately of any
                  unauthorized use of your account.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Intellectual Property
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  The Bossie application and all related content, including but
                  not limited to text, graphics, logos, and software, are owned
                  by Bossie and are protected by intellectual property laws.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Privacy
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Your privacy is important to us. Please review our Privacy
                  Policy, which also governs your use of the services, to
                  understand our practices.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Limitation of Liability
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Bossie shall not be liable for any indirect, incidental,
                  special, consequential, or punitive damages resulting from
                  your use of the services.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Changes to Terms
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We reserve the right to modify these terms at any time. We
                  will notify users of any material changes by posting the new
                  terms on this page.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Contact Information
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  If you have any questions about these Terms and Conditions,
                  please contact us at{" "}
                  <a
                    href="mailto:support@bossie.com"
                    className="text-primaryColor hover:underline"
                  >
                    support@bossie.com
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
